import { Box, CircularProgress, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../../app/store';
import LicitacaoPublicListFiltroForm from './LicitacaoPublicListFiltroForm';

import errorFormat from '../../../../../utils/errorFormat';
import { LicitacaoList } from '../types/Licitacao';
import { useGetLicitacoesPublicQuery } from '../create/licitacaoApiSlice';
import LicitacaoPublicCardForm from './LicitacaoPublicCardForm';

export function LicitacaoPublicList() {
  const [licitacoes, setlicitacoes] = useState<LicitacaoList[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  const loaderRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const filtro = useSelector((state: RootState) => state.licitacaoPublicListFiltro);

  // RTK Query
  const { data, isFetching, isLoading, error } = useGetLicitacoesPublicQuery({
    data_inicial: filtro.dataInicial,
    data_final: filtro.dataFinal,
    search: filtro.search,
    page,
    apply: filtro.apply
  });

  // Atualiza estado com os dados retornados
  useEffect(() => {
    if (!data) return;

    if (page === 1) {
      setlicitacoes(data.results);
    } else {
      setlicitacoes(prev => [...prev, ...data.results]);
    }

    setHasMore(Boolean(data.next));
    setInitialLoading(false);
  }, [data, page]);

  // Erro ao carregar
  useEffect(() => {
    if (error) {
      enqueueSnackbar('Erro ao carregar as notícias.', { variant: 'error' });
      const erroReturn: string = errorFormat(error);
      enqueueSnackbar(erroReturn, { variant: 'error' });
      console.log("Erro",error);
    }
  }, [error, enqueueSnackbar]);

  // Reset ao mudar filtros
  useEffect(() => {
    setlicitacoes([]);
    setPage(1);
    setHasMore(true);
    setInitialLoading(true);
  }, [filtro.apply]);

  // Scroll infinito
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isFetching && !initialLoading) {
          setPage(prev => prev + 1);
        }
      },
      { rootMargin: '200px' }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isFetching, initialLoading]);

  // Restaura a página salva para carregar as notícias corretas
  useEffect(() => {
    const scrollData = localStorage.getItem('scrollPublicacao');
    if (!scrollData) return;

    const { page: savedPage } = JSON.parse(scrollData);
    if (savedPage) {
      setPage(savedPage);
    }
  }, []);

  // Scroll restore com limite de tentativas e cancelamento
  useEffect(() => {
    const scrollData = localStorage.getItem('scrollPublicacao');
    if (!scrollData) return;

    const { posicao, data, idlicitacao } = JSON.parse(scrollData);
    const now = new Date();
    const savedTime = new Date(data);
    const timeDifference = now.getTime() - savedTime.getTime();

    if (timeDifference > 600000) {
      localStorage.removeItem('scrollPublicacao');
      return;
    }

    let attempts = 0;
    const maxAttempts = 20;
    let isCancelled = false;

    const tryScrollToLicitacao = () => {
      if (isCancelled) return;

      const el = document.getElementById(idlicitacao);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        localStorage.removeItem('scrollPublicacao');
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(tryScrollToLicitacao, 300);
      } else {
        console.warn('Não foi possível encontrar o elemento da notícia para scroll.');
        localStorage.removeItem('scrollPublicacao');
      }
    };

    tryScrollToLicitacao();

    return () => {
      isCancelled = true;
    };
  }, [licitacoes]);

  // Tela de carregamento inicial
  if (initialLoading && licitacoes.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <LicitacaoPublicListFiltroForm />

          {licitacoes.length === 0 && !isFetching && (
              <Typography variant="h6" align="center" sx={{ mt: 4 }}>
                Nenhum registro/licitação encontrada com os filtros aplicados.
              </Typography>
            )}

      {licitacoes.map(n => (
        <LicitacaoPublicCardForm key={n.id} licitacao={n} />
      ))}

      {isFetching && page > 1 && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 3 }} />}

      <div ref={loaderRef} />
    </Box>
  );
}
